import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { mergeProps } from '../../utils/react.js';
import { Box } from '../Box/Box.js';
import { List } from '../List/List.js';
import { MenuContext } from './MenuContext.js';
import { getActiveItem, handleNavKeys, handleSearchKeys, isElementDisabled, moveItem, getFirstItem } from './utils.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["children", "role"];
var ITEM_ROLES = {
  menu: ["menuitem", "menuitemradio", "menuitemcheckbox"],
  listbox: ["option"]
};
var Menu = /*#__PURE__*/React.forwardRef(function MenuList(props, ref) {
  var children = props.children,
    _props$role = props.role,
    role = _props$role === void 0 ? "menu" : _props$role,
    rest = _objectWithoutProperties(props, _excluded);
  var isFirstFocusRef = React.useRef(true);
  var menuRef = React.useRef(null);
  var itemRefs = React.useMemo(function () {
    return Array.from({
      length: props.menuItemCount
    }, function () {
      return /*#__PURE__*/React.createRef();
    });
  }, [props.menuItemCount]);
  var itemRoles = ITEM_ROLES[role];
  var getListItems = function getListItems() {
    return itemRefs.filter(function (item) {
      return item.current && itemRoles.includes(item.current.getAttribute("role") || "");
    }).map(function (item) {
      return item.current;
    });
  };
  var focusOnFirstItem = function focusOnFirstItem() {
    if (itemRefs.filter(function (item) {
      return item.current && !isElementDisabled(item.current);
    }).length === 0) return;
    moveItem(getFirstItem(getListItems()));
  };
  var handleFocus = function handleFocus(e) {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (isFirstFocusRef.current) {
      focusOnFirstItem();
    }
    isFirstFocusRef.current = false;
  };
  var handleKeyDown = function handleKeyDown(e) {
    var items = getListItems();
    var item = getActiveItem(menuRef.current, items);
    handleNavKeys(e, items, item);
    handleSearchKeys(e, items, item);
  };
  var getItemRender = function getItemRender(child, childIndex) {
    var item = child;
    return /*#__PURE__*/React.cloneElement(child, mergeProps({
      onFocus: item.props.onFocus,
      ref: item.ref
    }, {
      role: item.props.role ? item.props.role : itemRoles[0],
      ref: childIndex !== void 0 ? itemRefs[childIndex] : void 0,
      tabIndex: item.props.tabIndex ? item.props.tabIndex : -1
    }));
  };
  return /*#__PURE__*/jsx(MenuContext, {
    getItemRender: getItemRender,
    children: /*#__PURE__*/jsx(Box, _objectSpread(_objectSpread(_objectSpread({}, rest), mergeProps(_objectSpread(_objectSpread({}, rest), {}, {
      ref: ref
    }), {
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      ref: menuRef,
      role: role,
      tabIndex: 0
    })), {}, {
      as: List,
      children: children
    }))
  });
});
Menu.displayName = "Menu";

export { Menu };
